import Image from "@tiptap/extension-image";
import { ReactNodeViewRenderer } from "@tiptap/react";

export interface ImageResizeNodeOptions {
  /**
   * Allow resizing of images.
   * @default true
   */
  allowResize?: boolean;
  /**
   * Allow maintaining aspect ratio during resize.
   * @default true
   */
  maintainAspectRatio?: boolean;
  /**
   * Minimum width in pixels.
   * @default 50
   */
  minWidth?: number;
  /**
   * Minimum height in pixels.
   * @default 50
   */
  minHeight?: number;
  /**
   * Allow setting the alignment via data attribute
   * @default true
   */
  allowAlign?: boolean;
  /**
   * HTML attributes to add to the image element.
   * @default {}
   * @example { class: 'foo' }
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  HTMLAttributes?: Record<string, any>;
  isEditable?: boolean;
  onAttachmentRename?: (payload: {
    imageId: string;
    fileName: string;
    onRenamed?: () => void;
  }) => void;
  onAttachmentDelete?: (payload: { imageId: string }) => void;
  onAttachmentClick?: (payload: {
    url?: string;
    imageId?: string;
  }) => boolean;
}

/**
 * Extended Tiptap Image extension with resize handles.
 * Extends the default Image extension with custom NodeView for resizing.
 */
export const ImageResizeNode = Image.extend<ImageResizeNodeOptions>({
  addOptions() {
    return {
      ...this.parent?.(),
      allowResize: true,
      maintainAspectRatio: true,
      minWidth: 50,
      minHeight: 50,
      allowAlign: true,
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      imageId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-image-id"),
        renderHTML: (attributes) => {
          if (!attributes.imageId) return {};
          return {
            "data-image-id": attributes.imageId,
          };
        },
      },
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute("width"),
        renderHTML: (attributes) => {
          if (!attributes.width) return {};
          return {
            width: attributes.width,
          };
        },
      },
      height: {
        default: null,
        parseHTML: (element) => element.getAttribute("height"),
        renderHTML: (attributes) => {
          if (!attributes.height) return {};
          return {
            height: attributes.height,
          };
        },
      },
      allowResize: {
        default: this.options.allowResize,
      },
      maintainAspectRatio: {
        default: this.options.maintainAspectRatio,
      },
      minWidth: {
        default: this.options.minWidth,
      },
      minHeight: {
        default: this.options.minHeight,
      },
      isEditable: {
        default: this.options.isEditable,
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(
      require("app/components/TextEditor/components/tiptap-node/image-resize-node/image-resize-node")
        .ImageResizeNode,
    );
  },
});
